import { ParagraphComponent } from './paragraph/paragraph.component';
import { FormsComponent } from './forms/forms.component';
import { HeaderComponent } from './header/header.component';
import { InjectionToken, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { EnumsFields } from './store/enumeration/enumeration.model';
import { CancellationReasonConfig } from './transaction-status/transaction-status.model';

export abstract class Renderable {
  public abstract config: RenderableConfig;
  public abstract onInit$?: Observable<void>;
}

export interface StatesObj {
  code: string;
  name: any;
}

export interface CmsCountryObj {
  code: string;
  iso2code: string;
  name: string;
  states: StatesObj[];
}

export interface RenderableConfig {
  component: RenderableComponents;
  token: InjectionToken<RenderableConfig>;
}

export enum RenderableComponents {
  PARA,
  HEADER,
  FORM,
  ESTIMATE,
}

export const RENDERABLE_COMPONENTS: {
  [prop in RenderableComponents]?: Type<Renderable>;
} = {
  [RenderableComponents.PARA]: ParagraphComponent,
  [RenderableComponents.FORM]: FormsComponent,
  [RenderableComponents.HEADER]: HeaderComponent,
};

export interface StepperConfig {
  steps: StepConfig[];
}

export interface StepConfig {
  content: RenderableConfig[];
}

export interface StepFormWatchConfig {
  [stepIndex: number]: FormGroup;
}

export interface AppStateCountry {
  name: string;
  code: string;
}

export interface AppCountry extends AppStateCountry {
  iso2Code: string;
  iso3Code?: string;
  states?: AppStateCountry[];
  currency?: string;
  baseReceiveCurrency?: string;
  code: string;
  countryDialingCode: string;
  receiverInfoStateRequired?: boolean;
  name: string;
  postalCodePattern?: string;
}

export interface SectionItem {
  id_type?: string;
  name: string;
  value?: string | number;
  sendCurrency?: string;
  isBold?: boolean;
  displayMinus?: boolean;
  isDepositOnlyItem?: boolean;
  supNumber?: string;
}

export interface ItemSummarySection {
  name: string;
  value: any;
  sendCurrency?: string;
  isBold?: boolean;
  displayMinus?: boolean;
}
export interface AppSummarySectionConfig {
  title?: SectionItem;
  items: ItemSummarySection[];
  footer?: SectionItem;
}

export enum BreakPoints {
  XS = 'screen and (max-width: 767.5px)',
  GT_XS = 'screen and (min-width: 768px)',
}

export enum MobilBreakPoints {
  MW = 'screen and (max-width: 430px)',
  Tablet = 'screen and (max-width: 768px)'
}

export interface LanguageConfig {
  code: string;
  cmsKey: string;
}

export interface LabelDict {
  [labelKey: string]: string | LabelDict | undefined;
}

export interface EstimateLabels {
  header: {
    withdraw: string;
    deposit: string;
  };
  subheader: string;
  receiveStateProvince: string;
  cashOutCountry: string;
  cashInCountry: string;
  cashOutStateProvince: string;
  cashInStateProvince: string;
  cashOutState: string;
  cashInState: string;
  cashOutProvince: string;
  cashInProvince: string;
  amount: string;
  cashOutAmount: string;
  cashInAmount: string;
  noCashOutAllowed: string;
  returnToWallet: string;
  exchangeCurrency: string;
  indicativeCountryHelpText: string;
  note: string;
  currecyFootNote: string; // ToDo: correct spelling
  searchACountry: string;
}

export interface LocationLabels {
  findCashoutLocation: string;
  agentURLTextUK: string;
  findPaymentLocation: string;
  selectPaymentLocation: string;
  searchHelpText: string;
  selectLocationBtnTxt: string;
  selectLocation: string;
  openUntil: string;
  locationsUrl: string;
  findLocation: string;
  cashoutLocation: string;
  depositLocation: string;
  miles: string;
  addToPhone: string;
  bankAccountRequired: string;
  billPayment: string;
  distanceMItoKM: string;
  friHours: string;
  hours: string;
  hoursWarning: string;
  monHours: string;
  moneyOrder: string;
  purchaseMoneyorder: string;
  purchaseMoneygramCard: string;
  recCurrencies: string;
  receiveMoney: string;
  receiveToCard: string;
  reloadMoneygramCard: string;
  satHours: string;
  sendCurrencies: string;
  sendMoney: string;
  services: string;
  sunHours: string;
  thuHours: string;
  tueHours: string;
  wedHours: string;
  xpressPackage: string;
  agentURLTextFinland: string;
  backBtnTxt: string;
  changeLocation: string;
  closedOpenTxt: string;
  closedTxt: string;
  closingSoonTxt: string;
  currentlyOpenTxt: string;
  filterServiceTxt: string;
  findLocationTxt: string;
  hoursOperationTxt: string;
  locationsMatchedTxt: string;
  name: Name;
  noFollow: string;
  property: Property;
  relCanonicalLink: string;
  title: string;
  noLocationAvailable: string;
  openUntilTxt: string;
  searchInputTxt: string;
  selectPayLocationTxt: string;
  selectPickupLocationTxt: string;
  sendLocation: string;
  typeNewAddress: string;
  unableToLocation: string;
  viewMoreServicesTxt: string;
  kilometers: string;
}
export interface Name {
  description: string;
  robots: string;
  keywords: string;
}

export interface Property {
  title: string;
  description: string;
}

export interface EstimateConfirmationLabels {
  header: string;
  purchaseAmount: string;
  cashOutAmount: string;
  cashInAmount: string;
  costEstimate: string;
  receiveAmount: string;
  depositToWallet: string;
  withdrawalFromWallet: string;
  fees: string;
  taxes: string;
  discount: string;
  exchangeRate: string;
  totalCost: string;
  toPayAtLocation: string;
  paymentLocation: string;
  sendMoney: string;
  receiveMoney: string;
  sendCurrency: string;
  receiveCurrency: string;
  transactionDetails: string;
  depositAmount: string;
  withdrawalAmount: string;
  amountToPay: string;
  exchangeAmount: string;
}

export interface monthsLabels {
  APR: string;
  AUG: string;
  DEC: string;
  FEB: string;
  JAN: string;
  JUL: string;
  JUN: string;
  MAR: string;
  MAY: string;
  NOV: string;
  OCT: string;
  SEP: string;
}

export interface dateOfBirthComponentLabels {
  DAY: string;
  MONTH: string;
  YEAR: string;
  enterValidDOB: string;
  months: monthsLabels;
}

export interface RegistrationLabels {
  header: string;
  subheader: string;
  personalInfoHeader: string;
  firstName: string;
  middleName: string;
  lastName: string;
  secondLastName: string;
  dateOfBirth: string;
  addressInfoHeader: string;
  currentAddressInfoHeader: string;
  currentAddressAndPhoneNumber: string;
  address: string;
  addressLine2: string;
  city: string;
  country: string;
  state: string;
  postalCode: string;
  mobileNumber: string;
  mobileCountry: string;
  dateOfBirthComponent: dateOfBirthComponentLabels;
  errorMsgs: {
    required: string;
    pattern: string;
    maxLength: string;
    minLength: string;
    invalid: string;
    max: string;
    min: string;
    invalidPostalCode: string;
    onlyAlphanumeric: string;
  };
  review: string;
  confirmed: string;
  confirmationCheckBoxStrings: {
    stringOne: string;
    stringTwo: string;
  };
}

export interface GffpValidations {
  longerChar: string;
  mustLongChar: string;
  characters: string;
  required: string;
  invalid: string;
}

export interface GffpLabels1 {
  header: string;
  identificationDetails: string;
  idType: string;
  idNumber: string;
  countryOfIssue: string;
  stateProvinceOfIssue: string;
  personalDetails: string;
  countryOfBirth: string;
  reminderText: string;
  validations: GffpValidations;
  gffpLabels: GffpLabels2;
}

export interface GffpLabels2 {
  senderFirstName: string;
  senderMiddleName: string;
  senderLastName: string;
  senderSecondLastName: string;
  senderTitle: string;
  senderDateOfBirth: string;
  senderBirthCountryCode: string;
  senderOccupationCode: string;
  senderAdditionalTransactionDetailsPurposeOfTransactionCode: string;
  senderAdditionalTransactionDetailsSourceOfFundsCode: string;
  senderAdditionalTransactionDetailsProofOfFundsCode: string;
  senderAdditionalTransactionDetailsIntendedUseOfMGIServicesCode: string;
  senderAdditionalTransactionDetailsRelationshipToReceiver: string;
  senderPrimaryIdentificationTitle: string;
  senderSecondaryIdentificationTitle: string;
  senderAdditionalTransactionDetails: string;
  senderPrimaryIdentificationId: string;
  senderPrimaryIdentificationIssueCountryCode: string;
  senderPrimaryIdentificationIssueCountrySubdivisionCode: string;
  senderPrimaryIdentificationTypeCode: string;
  senderSecondaryIdentificationId: string;
  senderPrimaryIdentificationExpirationDate: string;
  senderSecondaryIdentificationIssueCountryCode: string;
  senderSecondaryIdentificationIssueCountrySubdivisionCode: string;
  senderSecondaryIdentificationTypeCode: string;
  senderAdditionalFamilyNamesMothersMaidenName: string;
  senderAdditionalFamilyNamesTitle: string;
  errorMsgs: {
    expirationDateError: string;
  };
  mapiFieldErrorMessages: MAPIFieldErrorMessages;
}

export interface MAPIFieldErrorMessages {
  typeCodeInputFieldMessage: string; //TODO: Remove from the CMS [Not being used]
  primaryIdInputFieldMessage: string;
  issueCountryCodeInputFieldMessage: string; //TODO: Remove from the CMS [Not being used]
  issueCountrySubdivisionCodeInputFieldMessage: string; //TODO: Remove from the CMS [Not being used]
}
export interface SummaryLabels {
  header: string;
  sender: string;
  receiver: string;
  name: string;
  sendCountry: string;
  sendStateProvince: string;
  receiveCountry: string;
  receiveStateProvince: string;
  transactionDetails: string;
  paymentMethod: string;
  receiveMethod: string;
  wallet: string;
  cash: string;
  cashPickup: string;
  costSummary: string;
  fees: string;
  taxes: string;
  discount: string;
  withdrawalFromWallet: string;
  totalCostAmountYouPay: string;
  totalCost: string;
  cashInAmount: string;
  cashOutAmount: string;
  receiveAmount: string;
  purchaseAmount: string;
  toPayAtLocation: string;
  depositToWallet: string;
  paymentLocation: string;
  submit: string;
  depositAmount: string;
  withdrawalAmount: string;
  amountToPay: string;
  country: string;
  stateProvince: string;
  personalInformation: string;
  paymentInformation: string;
  exchangeAmount: string;
}

export interface TermsAndConditionsLabels {
  and: string;
  disclaimerLabels: {
    termsAndConditionsLabel: string;
    privacyNotice: string;
    usdcUserAgreement: string;
    applicableUsePolicy: string;
    privacyPolicy: string;
  };
  disclaimerLinks: {
    circleAccpPolicy: string;
    circlePolicy: string;
    circleTerms: string;
    mgPrivacy: string;
    mgTandC: string;
  };
  cashOut: {
    webPara1: string;
    mobilePara1: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
    mgDisclaimerPara2: string;
  };
  cashIn: {
    webPara1: string;
    mobilePara1: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
    mgDisclaimerPara2: string;
  };
  mgAgreementPara3: {
    web: string;
    mobile: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
  };
}
export interface NewTermsAndConditionsLabels {
  and: string;
  disclaimerLabels: {
    termsAndConditionsLabel: string;
    privacyNotice: string;
    usdcUserAgreement: string;
    applicableUsePolicy: string;
    privacyPolicy: string;
  };
  disclaimerLinks: {
    circleAccpPolicy: string;
    circlePolicy: string;
    circleTerms: string;
    mgPrivacy: string;
    mgTandC: string;
  };
  cashOut: {
    webPara1: string;
    mobilePara1: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
    mgDisclaimerPara2: string;
  };
  cashIn: {
    webPara1: string;
    mobilePara1: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
    mgDisclaimerPara2: string;
  };
  mgAgreementPara3: {
    web: string;
    mobile: {
      stringOne: string;
      stringTwo: string;
      stringThree: string;
    };
  };
}

export interface ConfirmationLabels {
  header: string;
  referenceNumber: string;
  transactionNumber: string;
  nextSteps: string;
  helpText: string;
  sendLocation: string;
}

export interface depositNonFromFreeLabels {
  firstPoint: string;
  secondPoint: string;
  firstBulletInSecondPoint: string;
  secondBulletInSecondPoint: string;
  thirdBulletInSecondPoint: string;
  thirdPoint: string;
  firstBulletInThirdPoint: string;
  secondBulletInThirdPoint: string;
  fourthPoint: string;
}

export interface StatusUpdatesLabels {
  withdraw: {
    firstBulletVibrant: string;
    firstBulletLobstr: string;
    secondBullet: string;
  };
  deposit: {
    firstBullet: string;
    secondBullet: string;
    thirdBullet: string;
    secondBulletQRMobile: string;
    secondBulletQRDesktop: string;
  };
  depositNonFromFreeLabels: depositNonFromFreeLabels;
}

export interface ErrorLabels {
  isRequired: string;
  countryRequired: string;
  stateRequired: string;
  amountRequired: string;
  invalidAmount: string;
  exceedsTransactionLimit: string;
  somethingHasGoneWrong: string;
  processingError: string;
  notAvailableInCountry: string;
  amountExceededBalance: string;
  unableToProcess: string;
  walletNotEligible: string;
  unexpectedErrorContactMoneygram: string;
  maximumReceiveAmount: string;
  receiveAmountLessThanMin: string;
  maximumWithdrawalAmount: string;
}

export interface CancelLabels {
  header: string;
  referenceNumber: string;
  purchaseAmount: string;
  kind: string;
  cashOut: string;
  selectReason: string;
  cancelTransfer: string;
  cancelationSuccessful: string;
  cancelationSuccessfulHelpText: string;
  reasonNotFound: string;
}

export interface StatusLabels {
  incomplete: string;
  incompleteHelpText: string;
  refundOnly: string;
  expired: string;
  expiredHelpText: string;
  waitingForPayment: string;
  waitingForPaymentSubmitted: string;
  transactionProcessing: string;
  transactionReadyForPickup: string;
  readyForPayment: string;
  readyForPickup: string;
  readyForPickupHelpText: string;
  transactionPickedUp: string;
  transactionComplete: string;
  pendingRefund: string;
  transactionRefunded: string;
  transactionNumber: string;
  refunded: string;
  unknown: string;
  transactionRejected: string;
  onHoldContactMoneygram: string;
  unableToProcess: string;
}

export interface TransactionStatusLabels {
  status: StatusLabels;
  payForPurchase: string;
  CancelTransfer: string;
  referenceNumber: string;
  transactionNumber: string;
  receiver: string;
  sender: string;
  name: string;
  legalName: string;
  receiveCountry: string;
  receiveStateProvince: string;
  sendCountry: string;
  sendStateProvince: string;
  cash: string;
  cashOut: string;
  cashIn: string;
  cashOutAmount: string;
  cashOutCountry: string;
  cashOutState: string;
  cashOutProvince: string;
  paymentInformation: string;
  paymentLocation: string;
  kind: string;
  paymentMethod: string;
  receiveMethod: string;
  atLocation: string;
  walletId: string;
  walletTransactionId: string;
  costSummary: string;
  withdrawalFromWallet: string;
  depositToWallet: string;
  fees: string;
  discount: string;
  totalCostAmountYouPay: string;
  toPayAtLocation: string;
  agent: {
    tipsForAgent: string;
    helpText: string;
    agentWorks: string;
    deltaWorks: string;
  };
  contactMoneygramSupport: string;
}

export interface ServiceUnavailableLabels {
  accessDown: string;
  checkBackSoon: string;
  header: string;
}

export interface CustomerSupportLabels {
  phoneLabel: string;
  submitHelpRequest: string;
  onlineHelpForm: string;
  emailLabel: string;
  supportUrl: string;
  supportPhoneNumber: string;
  supportPhoneNumberHref: string;
  emailAddress: string;
}

export interface LanguageLabels {
  ARABIC: string;
  ENGLISH: string;
  FRENCH: string;
  PORTUGUESE: string;
  SPANISH: string;
  UKRAINIAN: string;
}

export interface FraudLabels {
  agreeAndContinue: string;
  beforeDeciding: string;
  fraudBulletOne: string;
  fraudBulletTwo: string;
  fraudBulletThree: string;
  fraudScenarios: string;
  pleaseBeAware: string;
  warning: string;
}

export interface StellarLabels {
  noToken: string;
  next: string;
  confirm: string;
  cancelText: string;
  ok: string;
  dismiss: string;
  close: string;
  submit: string;
  estimate: EstimateLabels;
  location: LocationLabels;
  estimateConfirmation: EstimateConfirmationLabels;
  registration: RegistrationLabels;
  gffp: GffpLabels1;
  summary: SummaryLabels;
  confirmation: ConfirmationLabels;
  statusUpdates: StatusUpdatesLabels;
  transactionStatus: TransactionStatusLabels;
  newTermsAndConditions: NewTermsAndConditionsLabels;
  termsAndConditions: TermsAndConditionsLabels;
  cancel: CancelLabels;
  error: ErrorLabels;
  enums: EnumsFields[];
  enumsObj: any;
  refundCancelReasons: CancellationReasonConfig[];
  excludesWalmart: string;
  serviceUnavailable: ServiceUnavailableLabels;
  stateNotFound: string;
  optionNotFound: string;
  country: {};
  countryNotFound: string;
  countryNotSupported: string;
  saveAsImage: string;
  postalCodeRequired: string;
  cellphoneRequired: string;
  telephoneRequired: string;
  customerSupport: CustomerSupportLabels;
  languageSelector: LanguageLabels;
  fraud: FraudLabels;
  sdfcard: SdfCardLabels;
  signIn: SignInLabels;
}

export interface StoreDetails {
  storeDetails: string;
  store: string;
  services: string;
  beforeYouGo: string;
  cashOnly: string;
  specificLocationHours: string;
  showStoreHours: string;
  hideStoreHours: string;
  hours: string;
}

export interface SdfCardLabels {
  common: {
    continue: string;
    done: string;
    yourPayAmount: string;
    yourWithdrawAmount: string;
    total: string;
    myInfo: string;
    legalName: string;
    dateOfBirth: string;
    address: string;
    phoneNumber: string;
    myId: string;
    idType: string;
    idNumber: string;
    idDetails: string;
    countryOfIssue: string;
    stateLabel: string;
    paymentLocation: string;
    selectLocation: string;
    selectState: string;
    selectCountry: string;
    changeLocation: string;
    fees: string;
    taxes: string;
    totalCost: string;
    totalAmount: string;
    withdrawnFromwallet: string;
    cancelTransaction: string;
    refundAmount: string;
    authNumber: string;
    refundStepsOne: string;
    refundStepsTwo: string;
    addedTowallet: string;
    youPaid: string;
    partnerWalletFee: string;
    partnerWalletFeeFootnote: string;
    notifcation: {
      one: string;
    };
    totalPayAmount: string;
    totalPickupAmount: string;
  };
  landingPage: {
    cashInHeadr: string;
    cashOutHeadr: string;
    cashInCountry: string;
    cashOutCountry: string;
    stateOrProvince: string;
    countrySelected: string;
    searchForCountryPlaceholder: string;
    searchForStateOrProvincePlaceholder: string;
    allCountries: string;
    allStatesOrProvinces: string;
    stateOrProvinceSelected: string;
    selectCurrency: string;
    lookupLocation: string;
    multiCurrency: string;
    disclaimer: {
      one: string;
      two: string;
      three: string;
    };
    payment: {
      cashPickUp: string;
      payMethod: string;
      receiveMethod: string;
      location: string;
      cashLocation: string;
      cashInlocationFeesInfo: string;
      cashOutlocationFeesInfo: string;
    };
  };
  locationMaps: {
    searchCityZipCode: string;
    findLocationNearby: string;
    searchArea: string;
    viewDetails: string;
    locationServices: string;
    popular: string;
    nearby: string;
    openNow: string;
    notification: {
      infoVariable: string;
      searchNoResult: string;
      locationUnavailable: string;
      tryAgainFuture: string;
    };
    storeDetails: StoreDetails;
  };

  moreInfoPage: {
    littleInfo: string;
    photoId: string;
    storeInfo: string;
  };
  basicInfoPage: {
    header: string;
  };
  addressPage: {
    header: string;
  };
  reviewPage: {
    reviewHeader: string;
    paymentLocation: string;
    paymentInformation: string;
    withdraw: {
      yourWithdrawalAmount: string;
    };
    deposit: {
      yourConvertingMethod: string;
    };
  };
  confirmationPage: {
    cashInStatusHeader: string;
    cashOutStatusHeader: string;
    payAtStore: string;
    amountAdding: string;
    amountWithdraw: string;
    pickAmount: string;
    receiver: string;
    howToPay: string;
    accountNumber: string;
    walletBalance: string;
    nextStepsHeading: string;
    nextSteps: {
      idType: {
        ALN: string;
        ALN_PREC: string;
        DRV: string;
        GOV: string;
        PAS: string;
        STA: string;
      };
      paraOne: string;
      paraTwo: string;
      tellTheStoreEmployee: string;
      fundsIn: {
        common: {
          yourNameAsReceiver: string;
          serviceOption: string;
        };
        paraThree: {
          implicitTransUSCAN: {
            purpose: string;
          };
          implicitNoneUSCAN: {
            purpose: string;
          };
          explicit: {
            paraTwo: string;
            purpose: string;
          };
        };
      };
      fundsOut: {
        paraOne: string;
        receiveCash: string;
      };
      onHold: {
        para1: string;
        para2: string;
      };
      senderRejected: {
        para1: string;
        para2CashIn: string;
        para2CashOut: string;
      };
    };
  };
}

export interface SignInLabels {
  continue: string,
  registerEmail: {
    enterYourEmail: string,
    email: string,
    reenterEmail: string,
    emailsMatch: string,
    emailsDoNotMatch: string
  },
  requestOtp: {
    enterMobileNumber: string,
    mobileNumber: string,
    requestCode: string
  },
  submitOtp: {
    enterSecurityCode: string,
    sentSecurityCodeSubtitle: string,
    codeWillBeValid: string,
    sendNewCode: string
  },
  userProfile: {
    enter: string,
    select: string,
    createNewProfile: string,
    createNewProfileSubtitle: string,
    firstName: string,
    lastName: string,
    middleName: string,
    findAddress: string,
    address: string,
    cityOrTown: string,
    country: string,
    state: string,
    postalCode: string,
    dateOfBirth: string,
    monthTwoChar: string,
    dayTwoChar: string,
    yearFourChar: string,
    countryOfBirth: string
  },
  paymentMethods: {
    [key: string]: string,
    enterFundsIn: string,
    enterFundsOut: string,
    paymentMethod: string,
    cashAtLocation: string,
    cashPickup: string,
    debitCard: string,
    bankAccount: string,
    fee: string,
    comingSoon: string,
    termsAndPrivacyPolicy: string
  }
}

export interface Labels {
  stellar: StellarLabels;
}
